/**
 * Logika wyznaczania okresu źródłowego dla kopiowania cen.
 * Zgodność dnia tygodnia z początkiem okresu docelowego zachowuje układ weekendów.
 */
import {
  addDays,
  subDays,
  getDay,
  differenceInCalendarDays,
  isBefore,
  isAfter,
  isSameDay,
  endOfMonth,
  getDate,
  getMonth,
  getYear,
  eachDay,
} from 'date-fns'

/** Długość okresu docelowego w dniach (włącznie). */
export function getTargetLengthInDays(targetFrom: Date, targetTo: Date): number {
  return differenceInCalendarDays(targetTo, targetFrom) + 1
}

/** Czy okres [fromA, toA] nachodzi na [fromB, toB]. */
export function rangesOverlap(
  fromA: Date,
  toA: Date,
  fromB: Date,
  toB: Date,
): boolean {
  return !(isAfter(fromA, toB) || isAfter(fromB, toA))
}

/** Data „ta sama w poprzednim roku”. 29.02 w roku bez 29.02 → ostatni dzień lutego. */
function sameDatePreviousYear(d: Date): Date {
  const y = getYear(d)
  const m = getMonth(d)
  const day = getDate(d)
  const prevYear = y - 1
  const candidate = new Date(prevYear, m, Math.min(day, 28))
  if (m === 1 && day === 29) {
    return endOfMonth(new Date(prevYear, 1, 1))
  }
  if (getDate(candidate) !== day && getMonth(candidate) === m) {
    return endOfMonth(new Date(prevYear, m, 1))
  }
  return candidate
}

/**
 * Opcja „Z roku poprzedniego”:
 * – początek: ta sama data w poprzednim roku, przesunięta o 0–3 dni na ten sam dzień tygodnia,
 * – długość jak okres docelowy,
 * – jeśli okres źródłowy nachodzi na docelowy, cofamy o pełne tygodnie.
 */
export function computePreviousYear(
  targetFrom: Date,
  targetTo: Date,
): { sourceFrom: Date; sourceTo: Date } {
  const length = getTargetLengthInDays(targetFrom, targetTo)
  const targetWeekday = getDay(targetFrom)

  let sourceFrom = sameDatePreviousYear(targetFrom)
  let sourceWeekday = getDay(sourceFrom)
  let shift = (targetWeekday - sourceWeekday + 7) % 7
  if (shift > 3) shift -= 7
  sourceFrom = addDays(sourceFrom, shift)

  let sourceTo = addDays(sourceFrom, length - 1)

  while (!isBefore(sourceTo, targetFrom)) {
    sourceFrom = subDays(sourceFrom, 7)
    sourceTo = subDays(sourceTo, 7)
  }

  return { sourceFrom, sourceTo }
}

/**
 * Opcja „Okres bezpośrednio przed”:
 * – okres o tej samej długości, kończący się tuż przed początkiem docelowego,
 * – początek w tym samym dniu tygodnia co targetFrom (ew. z przerwą, jeśli długość nie jest wielokrotnością 7).
 */
export function computeImmediatelyBefore(
  targetFrom: Date,
  targetTo: Date,
): { sourceFrom: Date; sourceTo: Date } {
  const length = getTargetLengthInDays(targetFrom, targetTo)
  const targetWeekday = getDay(targetFrom)
  const lastAllowedEnd = subDays(targetFrom, 1)
  let sourceTo = lastAllowedEnd
  let sourceFrom = subDays(sourceTo, length - 1)
  const sourceWeekday = getDay(sourceFrom)
  const shift = (sourceWeekday - targetWeekday + 7) % 7
  if (shift > 0) {
    sourceFrom = subDays(sourceFrom, shift)
    sourceTo = subDays(sourceTo, shift)
  }
  return { sourceFrom, sourceTo }
}

/**
 * Opcja „Własne”: koniec okresu = data od + (długość docelowa - 1).
 */
export function computeCustomEnd(
  customFrom: Date,
  targetFrom: Date,
  targetTo: Date,
): Date {
  const length = getTargetLengthInDays(targetFrom, targetTo)
  return addDays(customFrom, length - 1)
}

/** Czy data ma ten sam dzień tygodnia co targetFrom. */
export function isSameWeekday(d: Date, targetFrom: Date): boolean {
  return getDay(d) === getDay(targetFrom)
}

/** Czy wybrana „data od” daje okres bez nakładania na docelowy. */
export function customPeriodDoesNotOverlap(
  customFrom: Date,
  targetFrom: Date,
  targetTo: Date,
): boolean {
  const length = getTargetLengthInDays(targetFrom, targetTo)
  const customTo = addDays(customFrom, length - 1)
  return isBefore(customTo, targetFrom)
}

/**
 * Zwraca daty, które należy wyłączyć w kalendarzu dla opcji „Własne”:
 * – inny dzień tygodnia niż targetFrom,
 * – lub taki wybór, że okres źródłowy nachodzi na docelowy.
 */
export function getDisabledDaysForCustom(
  targetFrom: Date,
  targetTo: Date,
  rangeStart: Date,
  rangeEnd: Date,
): Date[] {
  const length = getTargetLengthInDays(targetFrom, targetTo)
  const targetWeekday = getDay(targetFrom)
  const days = eachDay(rangeStart, rangeEnd)
  return days.filter(day => {
    if (getDay(day) !== targetWeekday) return true
    const periodEnd = addDays(day, length - 1)
    return !isBefore(periodEnd, targetFrom)
  })
}

export function isSameDate(d1: Date, d2: Date): boolean {
  return isSameDay(d1, d2)
}
